/**
 * Per-service FAQs + engagement profile (typical length / day-rate band).
 *
 * Keyed by service.slug from /app/frontend/src/data/services.js.
 *
 * Day-rate bands are deliberately RANGES, not fixed prices — they qualify
 * inbound prospects without committing to a number. Adjust to taste.
 */

// A common closing FAQ used on every service so buyers always get the discovery details.
const SHARED_CLOSE = [
  {
    q: "How do we get started?",
    a: "Book a free 30-minute discovery conversation. Bring one challenge — we'll spend 30 focused minutes on it, then send a one-page written summary with three prioritised next steps within 24 hours. No slide pitch, no obligation.",
  },
  {
    q: "Do you work fixed-price or time-and-materials?",
    a: "Diagnostics and assessments are usually fixed-price with a clear deliverable. Programme support, interim leadership and ongoing advisory are typically day-rate. We agree the commercial model upfront so there are no surprises.",
  },
];

const PROFILES = {
  "ai-readiness-and-adoption": {
    pills: { length: "2–6 weeks", team: "1–2 Ekselens consultants", rateBand: "Diagnostic from £15k fixed" },
    faqs: [
      {
        q: "What's the difference between AI readiness and AI adoption?",
        a: "Readiness assesses whether your organisation has the strategy, data, governance, workforce and value-measurement foundations to use AI safely and at scale. Adoption is the practical work of designing, piloting and embedding specific use cases. We do both, but readiness usually comes first.",
      },
      {
        q: "How long does an AI readiness assessment take?",
        a: "Typically 2–4 weeks for a focused diagnostic (10–15 stakeholder interviews, data and risk review, prioritised use-case portfolio, 90-day plan). A full enterprise-wide assessment with workforce surveys and governance design can run 6–8 weeks.",
      },
      {
        q: "Do we need clean data before we start with AI?",
        a: "Not perfectly — but you do need to know which data each prioritised use case depends on, who owns it and whether it's fit for purpose. We map this in the assessment and surface the data dependencies that would block a pilot.",
      },
      {
        q: "Can you help us choose between Copilot, ChatGPT Enterprise, Gemini and open-source models?",
        a: "Yes. We provide vendor-neutral tooling reviews aligned to your use cases, data residency, regulatory posture and existing platforms. We have no resale commercial relationships, so the recommendation reflects your context, not ours.",
      },
      ...SHARED_CLOSE,
    ],
  },

  "digital-and-technology-transformation": {
    pills: { length: "8–24 weeks", team: "2–4 Ekselens consultants", rateBand: "Per programme — scoped" },
    faqs: [
      {
        q: "How do you avoid the 'expensive consulting deck that goes nowhere' problem?",
        a: "Every Ekselens engagement produces outputs your team can use the day after we leave — workshop-ready artefacts, decision logs, named owners. We deliberately work alongside your people rather than around them.",
      },
      {
        q: "Do you replace our internal team or work with them?",
        a: "Work with them. We typically bring 1–4 senior consultants who pair with your transformation, programme or technology leadership. The transfer of capability is built into the engagement.",
      },
      {
        q: "Can you take a struggling programme back on track?",
        a: "Yes. Our Programme & PMO Improvement and Governance & Delivery Control services are designed for this. The first step is usually a rapid health-check to surface the root causes before any restructure.",
      },
      ...SHARED_CLOSE,
    ],
  },

  "project-programme-and-pmo-improvement": {
    pills: { length: "4–12 weeks", team: "1–2 senior consultants", rateBand: "Health-check from £20k fixed" },
    faqs: [
      {
        q: "What does a 'PMO uplift' actually deliver?",
        a: "A decision-grade reporting cadence (not 30-tab status decks), a recovered backlog of stalled decisions, a portfolio prioritisation model your sponsors actually use, and a workable RAID/dependency picture. We focus on decisions, not administration.",
      },
      {
        q: "We have a PMO but it's not landing. Where do you usually start?",
        a: "With a 2-week health-check: a survey of sponsors and delivery leads, a sample of recent reports, a review of governance forums and a sample of escalations. We come back with the 3–5 changes that will move the needle.",
      },
      {
        q: "Do you provide interim PMO leadership?",
        a: "Yes, for time-bounded windows — typically 3–6 months — while you recruit a permanent head or recover a stuck programme.",
      },
      ...SHARED_CLOSE,
    ],
  },

  "business-change-and-adoption": {
    pills: { length: "6–16 weeks", team: "1–3 change consultants", rateBand: "Per workstream — scoped" },
    faqs: [
      {
        q: "What's the difference between change management and adoption?",
        a: "Change management is the process of preparing the organisation for a transition. Adoption is the measurable outcome — people actually using the new way of working, with the intended benefits realised. Ekselens delivers both, anchored to adoption metrics.",
      },
      {
        q: "We've already done the 'comms plan'. Why isn't change landing?",
        a: "Communication is not adoption. Most stalled changes fail because there's no measurable behavioural target, no named owner of the new behaviour, no friction-removal work, and no feedback loop. We assess all four in the first two weeks.",
      },
      ...SHARED_CLOSE,
    ],
  },

  "service-and-operating-model-transformation": {
    pills: { length: "8–20 weeks", team: "2–3 consultants", rateBand: "Per engagement — scoped" },
    faqs: [
      {
        q: "How is this different from a process re-engineering exercise?",
        a: "Process re-engineering looks at how work flows. Operating-model transformation also designs the organisation, governance, locations, sourcing, capabilities and technology that make the new way of working possible. We do the full picture or just the slice you need.",
      },
      {
        q: "Will you make people redundant?",
        a: "No — that's an internal decision. Our role is to design the target model and the path to it. We surface workforce implications openly and help you plan the transition; the people decisions stay with you.",
      },
      ...SHARED_CLOSE,
    ],
  },

  "supplier-and-technology-value-optimisation": {
    pills: { length: "4–10 weeks", team: "1–2 consultants", rateBand: "Diagnostic from £18k fixed — savings-share optional" },
    faqs: [
      {
        q: "What kind of savings do clients typically see?",
        a: "It varies by spend profile, but a representative range on technology and SaaS spend is 8–18% in the first 12 months from a combination of licence right-sizing, renewal re-negotiation, contract consolidation and exit of shelfware. We baseline and evidence every change.",
      },
      {
        q: "Can you work on a savings-share / contingent fee basis?",
        a: "Yes, for the optimisation phase, with clear baselining rules agreed upfront. Most clients prefer a fixed-fee diagnostic + day-rate execution, but a hybrid model is available.",
      },
      {
        q: "Do you renegotiate contracts directly with our suppliers?",
        a: "We can — either leading the negotiation or coaching your team through it. Either way, the commercial relationship stays with you; we don't take supplier referral fees.",
      },
      ...SHARED_CLOSE,
    ],
  },

  "it-service-management-and-operational-excellence": {
    pills: { length: "6–14 weeks", team: "1–2 consultants", rateBand: "Assessment from £20k fixed" },
    faqs: [
      {
        q: "Are you ITIL-aligned?",
        a: "Yes — ITIL 4 informs our practice. But we don't insist on the language or the framework if your organisation uses a different one. The outcomes (resolved tickets faster, fewer escalations, happier users) matter more than the framework name.",
      },
      {
        q: "Will we need to change our ITSM tool?",
        a: "Often no. Most maturity issues we see are process and ownership problems, not tool problems. If a tool change IS warranted, we'll say so and help you scope a vendor-neutral selection.",
      },
      ...SHARED_CLOSE,
    ],
  },

  "data-insight-and-performance": {
    pills: { length: "6–16 weeks", team: "1–3 consultants", rateBand: "Per use case — scoped" },
    faqs: [
      {
        q: "Are you a data engineering firm?",
        a: "No — we don't build pipelines. We define the use cases, governance, ownership and measurement model, then work alongside your engineering team (in-house or partner) to deliver. We're vendor-neutral on platform.",
      },
      {
        q: "How do you measure 'data maturity' in a useful way?",
        a: "Against business outcomes you actually care about — decision latency, forecast accuracy, regulatory reporting risk, customer experience metrics — not abstract maturity scales.",
      },
      ...SHARED_CLOSE,
    ],
  },

  "benefits-realisation-and-assurance": {
    pills: { length: "4–8 weeks", team: "1–2 consultants", rateBand: "Per programme — from £15k fixed" },
    faqs: [
      {
        q: "Why do so many programmes promise benefits but never realise them?",
        a: "Three common causes: benefits owners aren't named, baselines aren't captured before the change, and the measurement cadence dies with the programme. We address all three in our assurance framework.",
      },
      {
        q: "Can you assure a programme that's already in flight?",
        a: "Yes — that's our most common engagement type. We rebuild the benefits register, baseline against the right data and put in place an owner-led tracking cadence.",
      },
      ...SHARED_CLOSE,
    ],
  },

  "governance-and-delivery-control": {
    pills: { length: "4–10 weeks", team: "1–2 senior consultants", rateBand: "Per portfolio — scoped" },
    faqs: [
      {
        q: "What does 'delivery control' mean in practice?",
        a: "A clear decision rights map, a working risk and dependency picture, a forum cadence that drives decisions (not status), and an escalation path that actually escalates. We build these in the first 4 weeks then transition ownership.",
      },
      {
        q: "Do you provide independent assurance for board / audit committee?",
        a: "Yes. We can act as independent assurance partner across a programme or portfolio, providing structured reviews against agreed criteria.",
      },
      ...SHARED_CLOSE,
    ],
  },

  "capability-and-training-needs-analysis": {
    pills: { length: "4–8 weeks", team: "1–2 consultants", rateBand: "Per capability — scoped" },
    faqs: [
      {
        q: "Do you deliver training, or just assess the need?",
        a: "Primarily we design the capability framework and identify the gap. Delivery is usually through your L&D function or specialist training partners — we'll recommend partners but stay vendor-neutral.",
      },
      ...SHARED_CLOSE,
    ],
  },

  "stakeholder-engagement-and-communications": {
    pills: { length: "4–12 weeks", team: "1–2 consultants", rateBand: "Per workstream — scoped" },
    faqs: [
      {
        q: "How is this different from a marketing or PR firm?",
        a: "We focus on internal transformation stakeholders — sponsors, executive sponsors, regulators, programme boards, frontline managers and end-users. Our role is making the change land, not promoting the brand.",
      },
      ...SHARED_CLOSE,
    ],
  },
};

export const profileFor = (slug) => PROFILES[slug] || { pills: null, faqs: SHARED_CLOSE };

export default PROFILES;
